import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { Action, Alert, ConfirmModal, SwitchInput } from 'corso-admin-ui';
import {
  ClaimResolutionMethodEnum,
  CorsoClaimType,
  crewClaimTypeEnumName,
  CrewMerchantUi,
} from 'corso-types';
import { useCallback, useEffect } from 'react';
import {
  Controller,
  DefaultValues,
  FormProvider,
  useForm,
} from 'react-hook-form';
import { useBeforeUnload, useBlocker, useNavigate } from 'react-router-dom';
import api from '~/api';
import {
  ClaimCreateFormOutputValues,
  claimCreateFormSchema,
  CrewClaimFormInputValues,
  CrewClaimFormOutputValues,
  ShippingProtectionFormInputValues,
  ShippingProtectionFormOutputValues,
} from '~/features/orderLookup/claimCreateSchemas';
import { useClaimSource } from '~/hooks/useClaimSource';
import { useIntegrationSettingsData } from '~/hooks/useIntegrationSettings';
import useIsCorsoAdmin, { useIsCorsoCxAgent } from '~/hooks/useIsCorsoAdmin';
import { useReturnLocations } from '~/hooks/useReturnLocations';
import { useReturnSettings } from '~/hooks/useReturnSettings';
import { useSearchParam } from '~/hooks/useSearchParam';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import { toLowerCase } from '~/utils/casing';
import { ErrorResponse, isErrorMessage } from '~/utils/errorResponse';
import { CrewClaimFields } from './CrewClaimFields';
import CustomerFields from './CustomerInfoFields';
import ShippingAddressFields from './ShippingAddressFields';
import { ShippingProtectionClaimDisplay } from './ShippingProtectionClaimDisplay';
import { ShippingProtectionClaimFields } from './ShippingProtectionClaimFields';

/** @deprecated Need to unify claim source schemas. */ // TODO unify claim source schemas
type ClaimCreateSource =
  | 'Merchant_App'
  | 'Kustomer_App_Integration'
  | 'Zendesk_App_Integration';

const crewClaimLineItemFormValuesToClaimCreate =
  (
    order: CrewMerchantUi.CrewOrder,
    claimType: CrewClaimFormOutputValues['claimType'],
  ) =>
  (claimLineItem: CrewClaimFormOutputValues['claimLineItems'][number]) => {
    const lineItem = order.lineItems.find(
      (orderLineItem) =>
        orderLineItem.id === claimLineItem.originalStoreOrderLineItemId,
    );
    if (!lineItem) {
      throw new Error(
        `Line item ${claimLineItem.originalStoreOrderLineItemId} not found in order`,
      );
    }

    const claimReason = lineItem[toLowerCase(claimType)].claimReasons.find(
      ({ id }) => id === claimLineItem.reasonId,
    );
    if (!claimReason) {
      throw new Error(
        `Claim reason ${claimLineItem.reasonId} not found in line item ${lineItem.id}`,
      );
    }

    const claimReasonDetail = claimReason.claimReasonDetails.find(
      ({ id }) => id === claimLineItem.reasonDetailId,
    );
    if (!claimReasonDetail && claimLineItem.reasonDetailId) {
      throw new Error(
        `Claim reason detail ${claimLineItem.reasonDetailId} not found in line item ${lineItem.id}`,
      );
    }

    return {
      reasonCategoryCode: claimReason.category.code,
      reasonId: claimLineItem.reasonId,
      reasonDetailId: claimLineItem.reasonDetailId,
      originalStoreOrderLineItemId: claimLineItem.originalStoreOrderLineItemId,
      quantity: claimLineItem.quantity,
      comments: null,
      requestedResolutionMethodEnum:
        claimLineItem.requestedResolutionMethodEnum,
      advancedExchangeMethod: 'NO_PAYMENT_OR_REFUND',

      variantExchangeLineItem: claimLineItem.variantExchangeLineItem,
      unitPrice: lineItem.unitPrice,
      unitTax: lineItem.unitTax,

      // ! `amount` unavailable to represent the requested amount of the claim line item, this is only available for modification after the claim is created
    } satisfies CrewMerchantUi.ClaimCreate['claimLineItems'][number];
  };

const crewFormValuesToClaimCreate = ({
  order,
  formValues: { claimType, customer, shippingAddress, claimLineItems },
  source,
}: {
  order: CrewMerchantUi.CrewOrder;
  formValues: CrewClaimFormOutputValues;
  source: ClaimCreateSource;
}) =>
  ({
    source,
    customerEmail: customer.email,
    customerName: `${customer.firstName} ${customer.lastName}`.trim(),

    claimType,
    originalStoreOrderId: order.id,

    shippingAddress: {
      firstName: customer.firstName,
      lastName: customer.lastName,
      phone: customer.phone,
      ...shippingAddress,
    },
    // only the Shipping Address is validated on the claim create page
    billingAddress: {
      firstName: customer.firstName,
      lastName: customer.lastName,
      ...shippingAddress,
    },
    giftCardIncentiveAmountApplied: 0,
    exchangeOrderShippingAmountApplied: 0,
    feeApplied: 0,
    returnShippingAmountApplied: 0,
    claimLineItems: claimLineItems.map(
      crewClaimLineItemFormValuesToClaimCreate(order, claimType),
    ),
  }) satisfies CrewMerchantUi.ClaimCreate;

const shippingProtectionFormValuesToClaimCreate = ({
  order,
  formValues: { customer, shippingAddress, lineItems, ...rest },
  source,
}: {
  order: CrewMerchantUi.CrewOrder;
  formValues: ShippingProtectionFormOutputValues;
  source: ClaimCreateSource;
}) =>
  ({
    ...rest,
    shippingAddress: {
      ...shippingAddress,
      firstName: customer.firstName,
      lastName: customer.lastName,
      phone: customer.phone,
    },
    originalStoreOrder: {
      id: order.id,
      lineItems,
    },
    source,
  }) satisfies CrewMerchantUi.ShippingClaimCreate;

const useCreateClaim = (
  order: CrewMerchantUi.CrewOrder,
  claimType: CorsoClaimType,
  quickShipToAddressId?: number,
) => {
  const storeId = useStoreId();
  const navigate = useNavigate();
  const storeApi = api.store(storeId);
  const returnTo = useSearchParam('returnTo');
  const source = useClaimSource();
  const toast = useToast();

  return useMutation({
    mutationFn: async (formValues: ClaimCreateFormOutputValues) => {
      if (formValues.claimType === CorsoClaimType.shippingProtection) {
        return storeApi.shippingProtection.claimCreate(
          shippingProtectionFormValuesToClaimCreate({
            order,
            formValues,
            source,
          }),
        );
      }

      const createdClaim = await storeApi.claimCreate(
        crewFormValuesToClaimCreate({ order, formValues, source }),
      );

      const fromAddressId = createdClaim.shippingAddress.id;
      if (formValues.shouldQuickShip && quickShipToAddressId && fromAddressId) {
        // a failed label can't fail the claim, or the merchant re-submits into a duplicate
        await storeApi
          .claim(`${createdClaim.id}`)
          .quoteAndCreateShipment({
            kind: 'EasyPost_Vesyl',
            toAddressId: quickShipToAddressId,
            fromAddressId,
          })
          .catch((error: unknown) => {
            toast.show(
              isErrorMessage(error)
                ? error.message
                : 'Claim created, but the return label could not be generated.',
            );
          });
      }

      return createdClaim;
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: { claimCreate: claimType },
        extra: {
          orderId: order.id,
          status:
            error instanceof ErrorResponse ? error.response.status : undefined,
        },
      });
      // the claim may already exist server-side, so warn before a retry duplicates it
      toast.show(
        error instanceof ErrorResponse
          ? error.message
          : 'Something went wrong. Check the claims list before trying again.',
      );
    },
    onSuccess: (
      createdClaim: CrewMerchantUi.Claim | CrewMerchantUi.ShippingClaim,
    ) => {
      const claimSegment =
        claimType === CorsoClaimType.shippingProtection
          ? 'shipping'
          : claimType.toLowerCase();
      navigate(
        returnTo ?? `/${storeId}/claims/${claimSegment}/${createdClaim.id}`,
      );
    },
  });
};

const claimTypeLabels = {
  ...crewClaimTypeEnumName,
  [CorsoClaimType.shippingProtection]: 'Shipping Protection',
} as const;

const defaultValuesForCustomer = (order: CrewMerchantUi.CrewOrder) =>
  ({
    firstName: order.shippingAddress?.firstName,
    lastName: order.shippingAddress?.lastName,
    email: order.email,
    phone: order.shippingAddress?.phone,
  }) satisfies DefaultValues<ClaimCreateFormOutputValues['customer']>;

const defaultValuesForShippingAddress = (order: CrewMerchantUi.CrewOrder) =>
  ({
    line1: order.shippingAddress?.line1,
    line2: order.shippingAddress?.line2,
    city: order.shippingAddress?.city,
    stateOrProvinceCode: order.shippingAddress?.stateOrProvinceCode,
    postalCode: order.shippingAddress?.postalCode,
    countryCode: order.shippingAddress?.countryCode,
  }) satisfies DefaultValues<ClaimCreateFormOutputValues['shippingAddress']>;

const defaultValuesForClaimType = {
  [CorsoClaimType.shippingProtection]: (order: CrewMerchantUi.CrewOrder) => {
    if (!order.shippingClaimReasons[0]) {
      throw new Error('Shipping claim reasons not found in order');
    }

    return {
      lineItems: [],
      notifyCustomer: true,
      reason: order.shippingClaimReasons.at(0),
      resolutionMethod: ClaimResolutionMethodEnum.reorder,
    } satisfies DefaultValues<ShippingProtectionFormInputValues>;
  },
  [CorsoClaimType.return]: () =>
    ({
      claimLineItems: [],
      shouldQuickShip: false,
    }) satisfies DefaultValues<CrewClaimFormInputValues>,
  [CorsoClaimType.warranty]: () =>
    ({
      claimLineItems: [],
      shouldQuickShip: false,
    }) satisfies DefaultValues<CrewClaimFormInputValues>,
} as const;

export default function ClaimCreateForm({
  order,
  claimType,
  onCancel,
}: {
  order: CrewMerchantUi.CrewOrder;
  claimType: CorsoClaimType;
  onCancel: () => void;
}) {
  const { data: locations, isSuccess: areLocationsLoaded } =
    useReturnLocations();
  const { data: { easyPostConfig, vesylConfig } = {} } =
    useIntegrationSettingsData();

  const quickShipToAddressId = locations?.find(
    (location) => location.isQuickShipEnabled,
  )?.address.id;

  // Global-e generates its own label, so those claims quick ship from the claim detail page
  const canQuickShip =
    claimType !== CorsoClaimType.shippingProtection &&
    !order.globalEOrderId &&
    areLocationsLoaded &&
    !!quickShipToAddressId &&
    !!(easyPostConfig || vesylConfig);

  const {
    isPending,
    isSuccess,
    mutate: createClaim,
  } = useCreateClaim(order, claimType, quickShipToAddressId);

  const { data } = useReturnSettings();

  const isReturnProcessEnabled = data?.returns.isReturnProcessApiEnabled;

  const canCreateClaim =
    isReturnProcessEnabled && claimType === 'Return'
      ? order.isOrderFulfilled
      : true;

  const form = useForm({
    resolver: zodResolver(claimCreateFormSchema),
    defaultValues: {
      claimType,
      // shared
      customer: defaultValuesForCustomer(order),
      shippingAddress: defaultValuesForShippingAddress(order),

      // specific to claim type
      ...defaultValuesForClaimType[claimType](order),
    },
  });

  const blocker = useBlocker(form.formState.isDirty && !isPending);

  // the claim was created but the redirect to it was held by the unsaved-changes prompt
  useEffect(() => {
    if (blocker.state !== 'blocked' || !isSuccess) return;
    Sentry.captureMessage('Claim created but navigation was blocked', {
      level: 'warning',
      tags: { claimCreate: claimType },
      extra: { orderId: order.id, blockedPath: blocker.location.pathname },
    });
  }, [blocker, isSuccess, claimType, order.id]);

  const onBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!form.formState.isDirty) return;

      // cancel the event as stated by the standard
      event.preventDefault();
      /** set `returnValue` to help with compatibility @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#compatibility_notes */
      // oxlint-disable-next-line no-param-reassign
      event.returnValue = '';
    },
    [form.formState.isDirty],
  );

  useBeforeUnload(onBeforeUnload);

  const discardClaim = useCallback(() => {
    blocker.proceed?.(); // proceed if blocked
    form.reset();
    onCancel();
  }, [blocker, onCancel, form]);

  const isCorsoAdmin = useIsCorsoAdmin();
  const isCorsoCxAgent = useIsCorsoCxAgent();

  // TODO: Audit these returns + styles on the tooltip for shipping claim
  // ! Corso CX agents follow the store's unprotected order setting; only Corso admins override it
  const isAllowedToCreateShippingClaim =
    order.wasShippingProtected ||
    order.isUnprotectedShippingClaimPermitted ||
    !!order.wasCreatedFromReorder ||
    isCorsoAdmin;

  const existingShippingClaim = order.shippingClaims?.[0];

  //! don't block corso cx agents from creating a claim on an order that already has one
  if (
    claimType === CorsoClaimType.shippingProtection &&
    !!existingShippingClaim &&
    !isCorsoCxAgent
  ) {
    return (
      <>
        <ShippingProtectionClaimDisplay claim={existingShippingClaim} />
        <div className="flex flex-col gap-2 md:flex-row md:justify-end">
          <Action onClick={discardClaim}>Return to Lookup</Action>

          <Action
            variant="primary"
            to={`/${order.storeId}/claims/shipping/${existingShippingClaim.id}`}
          >
            View Claim
          </Action>
        </div>
      </>
    );
  }

  // ! only corso admins can override on non protected orders
  if (
    claimType === CorsoClaimType.shippingProtection &&
    !isAllowedToCreateShippingClaim
  ) {
    return (
      <>
        <Alert
          variant="warning"
          heading={`Order ${order.orderNo} was not protected, and is ineligible for a Shipping claim.`}
        />
        <div className="flex flex-col gap-2 md:flex-row md:justify-end">
          <Action onClick={discardClaim}>Return to Lookup</Action>
        </div>
      </>
    );
  }

  return (
    <>
      {order.shippingClaims?.map((shippingClaim) => (
        <Alert
          key={shippingClaim.id}
          heading="Duplicate Claim"
          variant="warning"
          dismissible
        >
          <div className="flex items-center justify-between gap-2">
            <p>{`Shipping claim #${shippingClaim.id} was already submitted for this order.`}</p>
            <Action
              to={`/${order.storeId}/claims/shipping/${shippingClaim.id}`}
            >
              View
            </Action>
          </div>
        </Alert>
      ))}

      <Alert
        variant={order.wasShippingProtected ? 'default' : 'warning'}
        heading={
          order.wasShippingProtected
            ? 'This order was protected.'
            : 'This order was not protected.'
        }
      />
      <FormProvider {...form}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            form
              .handleSubmit(
                (formFields) => createClaim(formFields),
                (fieldErrors) => console.warn(fieldErrors),
              )(e)
              .catch((error) => console.error(error));
          }}
        >
          <CustomerFields claimType={claimType} />
          <ShippingAddressFields />

          {claimType === CorsoClaimType.shippingProtection ? (
            <ShippingProtectionClaimFields order={order} />
          ) : (
            <CrewClaimFields
              order={order}
              claimType={claimType}
              canCreateClaim={canCreateClaim}
            />
          )}

          {canQuickShip && canCreateClaim && (
            <Controller
              control={form.control}
              name="shouldQuickShip"
              render={({ field: { onChange, value } }) => (
                <SwitchInput
                  id="quick-ship"
                  label="Quick Ship"
                  details="Immediately generate a return label using the default shipping provider, selecting the cheapest postage rate."
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          )}

          <div className="flex flex-col gap-2 md:flex-row md:justify-end">
            <Action
              type="submit"
              loading={isPending}
              variant="primary"
              disabled={!canCreateClaim}
            >
              Create {claimTypeLabels[claimType]} Claim
            </Action>
          </div>
        </form>

        <ConfirmModal
          title="Claim Creation Incomplete"
          prompt="You have an incomplete claim. Are you sure you want to discard them?"
          show={blocker.state === 'blocked'}
          onCancel={() => blocker.reset?.()}
          cancelText="Return to Claim"
          onConfirm={discardClaim}
          confirmText="Discard Claim"
        />
      </FormProvider>
    </>
  );
}
